import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';

import Button from '@klv/atoms/button';
import SelectInput from '@klv/molecules/form-field/select';
import TextareaInput from '@klv/molecules/form-field/textarea';
import { generateClassName } from '@klv/shared/utils/functions';
import { Wrapper } from '@klv/utils/wrapper';

import { useEmailSubjectLineGeneratorIndustries } from './hooks/useEmailSubjectLineGeneratorIndustries';
import * as styles from './styles/emailSubjectLineGenerator.module.css';

import type { IEmailSubjectLineGenerator } from './types';

export const EmailSubjectLineGenerator = ({
  backgroundColor = 'lightSand',
  customPadding = { customPaddingBottom: 'default', customPaddingTop: 'default' },
  fadeIn,
  formTitle,
  resultTitleCtaText,
  resultTitle,
}: IEmailSubjectLineGenerator) => {
  const { t } = useTranslation();
  const [results, setResults] = useState<string[]>([]);
  
  const industries = useEmailSubjectLineGeneratorIndustries();
  return (
    <Wrapper
      contained={true}
      grid={true}
      customPadding={customPadding}
      inlinePadding={true}
      fadeIn={fadeIn ? .25 : 0}
      backgroundColor={backgroundColor}
    >
      <Formik 
        initialValues={{ industry: 'beauty', campaignType: 'productLaunch', prompt: '' }}
        onSubmit={async (values) => {
          return new Promise((resolve) => {
            // get an array of the numebrs 1-12
            const entries: number[] = [...Array(10).keys()].slice(1);
            const randomResults: string[] = [];
            
            // get 4 random results and apply the related subject lines from the i18n file
            for (let i = 0; i < 4; i++) {
              const randomIndex: number = Math.floor(Math.random() * entries.length);
              randomResults.push(t(`organisms_emailSubjectLineGenerator_industry_options_${values.industry}_${values.campaignType}_result${entries[randomIndex]}`));
              entries.splice(randomIndex, 1);
            }
            setResults(randomResults);

            // simulate a loading state
            setTimeout(() => resolve(true), 2000);
          });
        }}>
          {({ values, setFieldValue, submitForm, isSubmitting, handleChange }) => {

            const clearResults = (e: React.ChangeEvent<HTMLSelectElement>) => {
              setResults([]);
              handleChange(e);
            };

            // Ensure that the campaign type is valid for the selected industry
            const currentIndustry = industries.find(({ industry }) => industry === values.industry);
            const currentCampaign = currentIndustry?.campaignTypes.find(({ type }) => type === values.campaignType);

            // Ensure that the campaign type is valid for the selected industry
            if (currentIndustry && !currentCampaign) {
              setFieldValue('campaignType', currentIndustry.campaignTypes[0].type);
            }

            // Ensure that the prompt is updated when the industry or campaign type changes
            if (currentCampaign && values.prompt !== currentCampaign.prompt) {
              setFieldValue('prompt', currentCampaign.prompt);
            }

            return (
              <>
                <div className={generateClassName([ styles.form])}>
                  <div className={generateClassName(['h5', styles.formTitle])}>
                    {formTitle}
                  </div>
                  <div className={styles.formWrapper}>
                    <div className={styles.inputWrapperHalf}>
                      <SelectInput onChange={clearResults} backgroundColor="light" name="industry" label={t('organisms_emailSubjectLineGenerator_industry')}>
                        {industries.map(({ industry }) => (
                          <option key={industry} value={industry}>
                            {t(`organisms_emailSubjectLineGenerator_industry_options_${industry}`)}
                          </option>
                        ))}
                      </SelectInput>
                    </div>

                    <div className={styles.inputWrapperHalf}>
                      <SelectInput onChange={clearResults} backgroundColor="light" name="campaignType" label={t('organisms_emailSubjectLineGenerator_campaignType')}>
                        {industries.map((industry) => {
                          if (industry.industry !== values.industry) return null;
                          return (industry.campaignTypes.map(({ type }) => (
                            <option key={type} value={type}>
                              {t(`organisms_emailSubjectLineGenerator_campaignType_${type}`)}
                            </option>
                          )));
                        })}
                      </SelectInput>
                    </div>

                    <div className={styles.inputWrapperFull}>
                      <TextareaInput 
                        backgroundColor="light"
                        label={t('organisms_emailSubjectLineGenerator_examplePrompt')}
                        name="prompt"
                        style={{ resize: 'none', pointerEvents: 'none' }}
                      />
                    </div>
                    
                    <div className={generateClassName([styles.inputWrapperFull, styles.alignRight])}>
                      <Button 
                        buttonColor='dark'
                        action={submitForm}
                        iconColor='white'
                        leftIcon={{ iconName: 'AiStar' }}
                        text={t('organisms_emailSubjectLineGenerator_submit')}
                      />
                    </div>
                  </div>
                </div>
                {/** Display the loading state */}
                {isSubmitting && (
                  <div className={styles.loading}>
                    <div className={styles.loadingBar}></div>
                    <div className="p3">{t('organisms_emailSubjectLineGenerator_loading')}</div>
                  </div>
                )}
                {/** Display the results */}
                {results.length > 0 && !isSubmitting && (
                  <div className={styles.results}>
                    <div className={generateClassName([styles.resultTitleWrapper, styles.fadeInUp])}>
                      <div className={generateClassName(['h5', styles.resultTitle])}>{resultTitle}</div>
                      <div className={styles.resultTitleCtaText} dangerouslySetInnerHTML={{ __html: resultTitleCtaText }} />
                    </div>
                    
                    {/** Add an incremental animation delay to each entry. */}
                    {results.map((entry, index) => (
                      <div
                        key={entry}
                        style={{ animationDelay: `${((index + 1) * .1)}s` }}
                        className={generateClassName([styles.result, styles.fadeInUp])}
                        dangerouslySetInnerHTML={{ __html: entry }}
                      />
                    ))}
                  </div>
                )}
              </>
            );
          }
        }
      </Formik>
    </Wrapper>
  );
};
